/**
 * VoucherIdentifiersSection - voucher and confirmation numbers on
 * Settings > Identifiers, for tenants with the vouchers module.
 *
 * Each number is an optional fixed prefix, the claim's reference (voucher
 * numbers only) and random digits. Random digits are what make a number
 * hard to guess, so they are always there.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  useUpdateVoucherIdentifierScheme,
  type VoucherIdentifiers,
  type VoucherIdentifierScheme,
  type VoucherIdentifierType,
} from '@/admin/hooks/use-identifier-config'
import { Button } from '@/shared/components/ui/button'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormField } from '@/shared/lib/forms'

const SAMPLE_DIGITS = '4817296305'
// How an unnumbered claim's reference looks, for a tenant with no claims yet.
const SAMPLE_PARENT = 'CLM-A1B2C3D4'
const MIN_DIGITS = 4
const MAX_DIGITS = 10

interface Shape {
  prefix: string
  withClaim: boolean
  digits: number
}

function shapeOf(scheme: VoucherIdentifierScheme): Shape {
  const prefix = scheme.segment_order.includes('prefix')
    ? (scheme.codes.find(
        (c) => c.slot === 'prefix' && c.source_kind === 'static',
      )?.code ?? '')
    : ''
  return {
    prefix,
    withClaim: scheme.segment_order.includes('parent'),
    digits: scheme.padding,
  }
}

export function VoucherIdentifiersSection({
  vouchers,
}: {
  vouchers: VoucherIdentifiers
}) {
  const { t } = useTranslation()
  return (
    <section
      className="mt-12 border-t-[0.5px] border-t-[var(--rule)] pt-8"
      data-testid="voucher-identifiers"
    >
      <p className="clara-section-label mb-2">
        {t('identifiers.vouchers.title', 'Vouchers')}
      </p>
      <p className="clara-small mb-6 max-w-[60ch]">
        {t(
          'identifiers.vouchers.lede',
          'The numbers on vouchers and on redemption confirmations. A change applies to vouchers made from now on.',
        )}
      </p>
      <div className="flex flex-col gap-8">
        <VoucherSchemeForm
          type="voucher"
          scheme={vouchers.schemes.voucher}
          sampleParent={vouchers.sample_parent || SAMPLE_PARENT}
        />
        <VoucherSchemeForm
          type="voucher_confirm"
          scheme={vouchers.schemes.voucher_confirm}
          sampleParent=""
        />
      </div>
    </section>
  )
}

function VoucherSchemeForm({
  type,
  scheme,
  sampleParent,
}: {
  type: VoucherIdentifierType
  scheme: VoucherIdentifierScheme
  sampleParent: string
}) {
  const { t } = useTranslation()
  const save = useUpdateVoucherIdentifierScheme()
  const isVoucher = type === 'voucher'
  const formId = `voucher-identifier-${type}`
  const initial = useMemo(() => shapeOf(scheme), [scheme])
  // The specimen follows the fields as they change.
  const [preview, setPreview] = useState<Shape>(initial)

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'prefix',
        label: t('identifiers.vouchers.prefix', 'Fixed prefix'),
        value: initial.prefix,
        maxLength: 8,
        hint: t(
          'identifiers.vouchers.prefixHint',
          'Optional. Up to 8 letters and digits.',
        ),
        onChange: (value) =>
          setPreview((p) => ({
            ...p,
            prefix: String(value ?? '')
              .trim()
              .toUpperCase(),
          })),
        validationSchema: z
          .string()
          .trim()
          .regex(
            /^[A-Za-z0-9]{0,8}$/,
            t(
              'identifiers.vouchers.prefixInvalid',
              'Letters A-Z and digits 0-9 only.',
            ),
          ),
      },
      ...(isVoucher
        ? [
            {
              type: 'checkbox' as const,
              name: 'with_claim',
              label: t(
                'identifiers.vouchers.withClaim',
                "Include the claim's reference",
              ),
              value: initial.withClaim,
              onChange: (value: unknown) =>
                setPreview((p) => ({ ...p, withClaim: Boolean(value) })),
            },
          ]
        : []),
      {
        type: 'number',
        name: 'digits',
        label: t('identifiers.vouchers.digits', 'Random digits'),
        value: initial.digits,
        hint: t(
          'identifiers.vouchers.digitsHint',
          'Drawn fresh for every number, 4 to 10 of them.',
        ),
        onChange: (value) =>
          setPreview((p) => ({ ...p, digits: Number(value) || p.digits })),
        validationSchema: z.coerce
          .number()
          .int(
            t(
              'identifiers.vouchers.digitsInvalid',
              'Choose between 4 and 10 digits.',
            ),
          )
          .min(
            MIN_DIGITS,
            t(
              'identifiers.vouchers.digitsInvalid',
              'Choose between 4 and 10 digits.',
            ),
          )
          .max(
            MAX_DIGITS,
            t(
              'identifiers.vouchers.digitsInvalid',
              'Choose between 4 and 10 digits.',
            ),
          ),
      },
    ],
    [t, initial, isVoucher],
  )

  const { Form } = useForm(
    { name: formId, submit: 'footer', fields },
    async (values) => {
      const v = values as Record<string, unknown>
      const prefix = String(v.prefix ?? '')
        .trim()
        .toUpperCase()
      const withClaim = isVoucher && Boolean(v.with_claim)
      try {
        await save.mutateAsync({
          resourceType: type,
          scheme: {
            is_enabled: true,
            segment_order: [
              ...(prefix ? ['prefix'] : []),
              ...(withClaim ? ['parent'] : []),
              'random',
            ],
            prefix_source: 'static',
            code_source: '',
            year_mode: 'none',
            padding: Number(v.digits),
            start_at: null,
            codes: prefix
              ? [
                  {
                    slot: 'prefix',
                    source_kind: 'static',
                    ref_key: '',
                    code: prefix,
                  },
                ]
              : [],
          },
        })
        toast.success(
          t('identifiers.vouchers.saved', 'Voucher numbering saved.'),
        )
      } catch (err) {
        toastApiError(
          err,
          t(
            'identifiers.vouchers.failed',
            'Voucher numbering could not be saved.',
          ),
        )
      }
    },
    [type, isVoucher, save, t],
  )

  const digits = Math.min(Math.max(preview.digits, MIN_DIGITS), MAX_DIGITS)
  const specimen = [
    preview.prefix,
    isVoucher && preview.withClaim ? sampleParent : '',
    SAMPLE_DIGITS.slice(0, digits),
  ]
    .filter(Boolean)
    .join('-')

  return (
    <div data-testid={`voucher-identifier-${type}`}>
      <p className="text-[13px] text-[var(--ink)]">
        {isVoucher
          ? t('identifiers.vouchers.voucherNumbers', 'Voucher numbers')
          : t(
              'identifiers.vouchers.confirmationNumbers',
              'Confirmation numbers',
            )}
      </p>
      <p className="clara-small mt-0.5">
        {isVoucher
          ? t(
              'identifiers.vouchers.voucherNumbersHint',
              'What the holder quotes to redeem the voucher.',
            )
          : t(
              'identifiers.vouchers.confirmationNumbersHint',
              'Given when a voucher is redeemed.',
            )}
      </p>
      <div
        className="mt-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3.5 font-mono text-[20px] text-[var(--ink)]"
        data-testid={`voucher-identifier-specimen-${type}`}
      >
        {specimen}
      </div>
      <Form className="mt-4 flex flex-col gap-4" />
      <div className="mt-4 flex justify-end">
        <Button type="submit" form={formId} size="sm" disabled={save.isPending}>
          {save.isPending && <Loader2 className="animate-spin" />}
          {t('common.save', 'Save')}
        </Button>
      </div>
    </div>
  )
}
